import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  VStack,
  HStack,
  Button,
  Divider,
  Badge,
  Input,
  FormControl,
  FormLabel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
  Radio,
  RadioGroup,
  Stack as CStack,
  Tooltip,
  Link,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon
} from '@chakra-ui/react'
import { FaLock, FaExchangeAlt } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import CheckAuth from '../components/layouts/CheckAuth'
import { useRef, useState, useMemo, useEffect } from 'react'
import BigNumber from 'bignumber.js'
import {
  cancelWithdrawal,
  getRecentWithdrawal,
  withdrawal
} from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import { FiCopy, FiExternalLink } from 'react-icons/fi'
import { useClipboard } from '@chakra-ui/react'

type Network = 'TRC20' | 'BSC'

const FEE_RATE = 0.05 // 5%
const FEE_PERCENT = new BigNumber(FEE_RATE).multipliedBy(100).toNumber()
const MIN_WITHDRAW = new BigNumber(100)

const Withdrawal = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { reloadUser } = useAuthState()

  const [network, setNetwork] = useState<Network>('BSC')
  const [address, setAddress] = useState('')
  const [amountStr, setAmountStr] = useState('') // 원본 입력 유지

  const amountBN = useMemo(() => {
    const v = new BigNumber(amountStr || 0)
    return v.isFinite() && v.gte(0) ? v : new BigNumber(0)
  }, [amountStr])

  const feeBN = useMemo(() => amountBN.multipliedBy(FEE_RATE), [amountBN])
  const receiveBN = useMemo(() => amountBN.minus(feeBN), [amountBN])

  const balanceBN = useMemo(
    () => new BigNumber(auth?.usdtBalance || 0),
    [auth?.usdtBalance]
  )

  const hasMin = amountBN.gte(MIN_WITHDRAW)
  const hasBalance = balanceBN.gte(amountBN)
  const validAddress =
    network === 'TRC20'
      ? /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address.trim()) // Tron base58 대략 검사
      : /^0x[a-fA-F0-9]{40}$/.test(address.trim()) // EVM 주소
  const formValid =
    amountBN.gt(0) && hasMin && hasBalance && validAddress && receiveBN.gt(0)

  // 퍼센트 채우기
  const fillAmount = (ratio: number) => {
    const target = balanceBN
      .multipliedBy(ratio)
      .decimalPlaces(2, BigNumber.ROUND_DOWN)
    setAmountStr(target.toString())
  }
  const fillMax = () => {
    setAmountStr(balanceBN.decimalPlaces(2, BigNumber.ROUND_DOWN).toString())
  }

  // 확인 다이얼로그
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!formValid || isSubmitting) return
    setIsSubmitting(true)
    try {
      const payload = {
        network, // 'TRC20' | 'BSC'
        address: address.trim(),
        amount: amountBN.toString() // 서버 재검증 전제
      }
      const { data: res } = await withdrawal(payload)
      if (res.statusCode === 200) {
        toast({
          title: t('출금 신청 완료'),
          description: res.message || t('출금이 접수되었습니다.'),
          status: 'success',
          duration: 2500,
          isClosable: true
        })
        onConfirmClose()
        reloadUser()
        run()
        setAmountStr('0')
        setAddress('')
      } else {
        throw new Error(res.message || t('출금 신청에 실패했습니다.'))
      }
    } catch (err: any) {
      toast({
        title: t('출금 실패'),
        description:
          err?.response?.data?.message ||
          err?.message ||
          t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  type WithdrawalHistory = {
    id: number
    type: 'WD_WAIT' | 'WD_OK' | 'WD_NO' | 'WD_RB'
    network: string | null
    walletAddress: string | null
    withdrawalAmount: string | null
    fee: string | null
    txid: string | null
    createdAt: string
  }

  const [recent, setRecent] = useState<WithdrawalHistory[]>([])
  const [loadingRecent, setLoadingRecent] = useState(false)

  const statusMeta: Record<
    WithdrawalHistory['type'],
    { label: string; color: string }
  > = {
    WD_WAIT: { label: t('대기'), color: 'yellow' },
    WD_OK: { label: t('완료'), color: 'green' },
    WD_NO: { label: t('취소'), color: 'red' },
    WD_RB: { label: t('회수'), color: 'cyan' }
  }

  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [targetId, setTargetId] = useState<number | null>(null)

  const openCancel = (id: number) => {
    setTargetId(id)
    setIsCancelOpen(true)
  }
  const closeCancel = () => {
    setIsCancelOpen(false)
    setTargetId(null)
  }

  // 최근 목록 재조회 헬퍼
  const refreshRecent = async () => {
    try {
      setLoadingRecent(true)
      const { data: res } = await getRecentWithdrawal()
      setRecent(res.data || [])
    } finally {
      setLoadingRecent(false)
    }
  }

  const handleCancelWithdrawal = async () => {
    if (!targetId || isCancelling) return
    setIsCancelling(true)
    try {
      const { data: res } = await cancelWithdrawal({ historyId: targetId })
      if (res.statusCode === 200) {
        toast({
          title: t('출금 취소 완료'),
          description: res.message || t('요청이 취소되었습니다.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        closeCancel()
        await refreshRecent()
        reloadUser()
        run()
      } else {
        throw new Error(res.message || t('취소에 실패했습니다.'))
      }
    } catch (err: any) {
      toast({
        title: t('출금 취소 실패'),
        description:
          err?.response?.data?.message || err?.message || t('알 수 없는 오류'),
        status: 'error',
        duration: 2500,
        isClosable: true
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const run = async () => {
    try {
      setLoadingRecent(true)
      const { data: res } = await getRecentWithdrawal()
      setRecent(res.data || [])
    } catch (e) {
      // silent
    } finally {
      setLoadingRecent(false)
    }
  }

  useEffect(() => {
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getExplorerTxUrl = (network?: string | null, txid?: string | null) => {
    if (!txid) return null
    if (network === 'BSC') return `https://bscscan.com/tx/${txid}`
    if (network === 'TRC20') return `https://tronscan.org/#/transaction/${txid}`
    return null
  }

  const CopyTxButton: React.FC<{ value: string }> = ({ value }) => {
    const { hasCopied, onCopy } = useClipboard(value)
    return (
      <Tooltip label={hasCopied ? t('복사됨!') : t('TXID 복사')}>
        <IconButton
          aria-label={t('TXID 복사')}
          size="xs"
          variant="ghost"
          onClick={onCopy}
          icon={<FiCopy />}
        />
      </Tooltip>
    )
  }

  const feeLabel = t('수수료({{p}}%)', { p: FEE_PERCENT.toString() })
  const topBadgeText = t('수수료 {{p}}% • 최소 {{m}} USDT', {
    p: FEE_PERCENT.toString(),
    m: MIN_WITHDRAW.toFormat(0)
  })

  return (
    <DefaultLayout>
      <CheckAuth />
      <Container maxW="lg" py={8}>
        <VStack align="stretch" spacing={6}>
          <HStack justify="space-between">
            <HStack>
              <FaExchangeAlt />
              <Text fontSize="xl" fontWeight="bold">
                {t('USDT 출금')}
              </Text>
            </HStack>
            <Badge colorScheme="cyan">{topBadgeText}</Badge>
          </HStack>

          <Box p={6} borderWidth="1px" borderRadius="2xl" bg="dark.card" borderColor="dark.border" shadow="lg">
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Text color="gray.400">{t('보유 USDT')}</Text>
                <Text fontWeight="bold" fontSize="lg" color="white">{balanceBN.toFormat(2)} USDT</Text>
              </HStack>

              <FormControl>
                <FormLabel>{t('네트워크')}</FormLabel>
                <RadioGroup
                  value={network}
                  onChange={(v) => setNetwork(v as Network)}
                >
                  <CStack direction="row" spacing={6}>
                    <Radio value="BSC">{t('BSC (BNB Chain)')}</Radio>
                    <Radio value="TRC20">{t('TRC20 (TRON)')}</Radio>
                  </CStack>
                </RadioGroup>
              </FormControl>

              <FormControl isInvalid={address.length > 0 && !validAddress}>
                <FormLabel>{t('수령 주소')}</FormLabel>
                <Input
                  placeholder={
                    network === 'TRC20'
                      ? t('T로 시작하는 TRON 주소')
                      : t('0x로 시작하는 BSC 주소')
                  }
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                {!validAddress && address.length > 0 && (
                  <Text mt={1} fontSize="sm" color="red.500">
                    {network === 'TRC20'
                      ? t('유효한 TRON 주소 형식이 아닙니다.')
                      : t('유효한 BSC 주소 형식이 아닙니다.')}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>{t('출금 금액 (USDT)')}</FormLabel>
                <HStack>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder={t('예: 100.00') as string}
                    value={amountStr}
                    onChange={(e) => setAmountStr(e.target.value)}
                  />
                  <Button size="sm" onClick={() => fillAmount(0.25)} variant="outline" borderColor="whiteAlpha.400" color="gray.300" _hover={{ bg: 'whiteAlpha.100' }}>
                    25%
                  </Button>
                  <Button size="sm" onClick={() => fillAmount(0.5)} variant="outline" borderColor="whiteAlpha.400" color="gray.300" _hover={{ bg: 'whiteAlpha.100' }}>
                    50%
                  </Button>
                  <Button size="sm" onClick={() => fillAmount(0.75)} variant="outline" borderColor="whiteAlpha.400" color="gray.300" _hover={{ bg: 'whiteAlpha.100' }}>
                    75%
                  </Button>
                  <Button size="sm" onClick={fillMax} variant="outline" borderColor="whiteAlpha.400" color="gray.300" _hover={{ bg: 'whiteAlpha.100' }}>
                    {t('최대')}
                  </Button>
                </HStack>
                <HStack
                  justify="space-between"
                  mt={2}
                  color="gray.400"
                  fontSize="sm"
                >
                  <Text>{feeLabel}</Text>
                  <Text>
                    {feeBN.decimalPlaces(2, BigNumber.ROUND_DOWN).toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
                <HStack justify="space-between" mt={1} fontWeight="bold">
                  <Text>{t('실수령액')}</Text>
                  <Text>
                    {receiveBN
                      .decimalPlaces(2, BigNumber.ROUND_DOWN)
                      .toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
                {!hasMin && amountBN.gt(0) && (
                  <Text mt={1} fontSize="sm" color="red.500">
                    {t('최소 출금 금액은 {{m}} USDT 입니다.', {
                      m: MIN_WITHDRAW.toFormat(0)
                    })}
                  </Text>
                )}
                {!hasBalance && amountBN.gt(0) && (
                  <Text mt={1} fontSize="sm" color="red.500">
                    {t('보유 잔액이 부족합니다.')}
                  </Text>
                )}
              </FormControl>

              <Divider />

              <VStack
                align="stretch"
                spacing={2}
                fontSize="sm"
                color="gray.400"
              >
                <HStack justify="space-between">
                  <Text>{t('출금 금액')}</Text>
                  <Text>
                    {amountBN.decimalPlaces(2, BigNumber.ROUND_DOWN).toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>{feeLabel}</Text>
                  <Text>
                    {feeBN.decimalPlaces(2, BigNumber.ROUND_DOWN).toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
                <HStack justify="space-between" fontWeight="bold">
                  <Text>{t('실수령액')}</Text>
                  <Text>
                    {receiveBN
                      .decimalPlaces(2, BigNumber.ROUND_DOWN)
                      .toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
              </VStack>

              <Button
                mt={4}
                colorScheme="cyan"
                isDisabled={!formValid}
                onClick={onConfirmOpen}
                leftIcon={<FaLock />}
              >
                {t('출금 신청')}
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* 확인 다이얼로그 */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="dark.card" borderColor="dark.border" borderWidth="1px">
            <AlertDialogHeader>
              {t('출금 내용을 확인해 주세요')}
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text>{t('네트워크')}</Text>
                  <Badge>{network}</Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('수령 주소')}</Text>
                  <Text maxW="260px" isTruncated>
                    {address}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('출금 금액')}</Text>
                  <Text>
                    {amountBN.decimalPlaces(2, BigNumber.ROUND_DOWN).toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>{feeLabel}</Text>
                  <Text>
                    {feeBN.decimalPlaces(2, BigNumber.ROUND_DOWN).toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
                <HStack justify="space-between" fontWeight="bold">
                  <Text>{t('실수령액')}</Text>
                  <Text>
                    {receiveBN
                      .decimalPlaces(2, BigNumber.ROUND_DOWN)
                      .toFormat()}{' '}
                    USDT
                  </Text>
                </HStack>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="ghost">
                {t('취소')}
              </Button>
              <Button
                colorScheme="cyan"
                ml={3}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText={t('신청 중...')}
              >
                {t('출금 신청')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 최근 출금 내역 */}
      <Container maxW="lg" py={4}>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="bold">
              {t('최근 출금 내역')}
            </Text>
            <Badge colorScheme="gray">{t('최근 10건')}</Badge>
          </HStack>

          <Box borderWidth="1px" borderRadius="2xl" bg="dark.card" borderColor="dark.border" overflowX="auto" shadow="lg">
            {loadingRecent ? (
              <Box p={6}>
                <Text color="gray.400">{t('불러오는 중...')}</Text>
              </Box>
            ) : recent.length === 0 ? (
              <Box p={6}>
                <Text color="gray.400">
                  {t('조회된 출금 내역이 없습니다.')}
                </Text>
              </Box>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('동작')}</Th>
                    <Th>{t('일시')}</Th>
                    <Th>{t('상태')}</Th>
                    <Th isNumeric>{t('금액(USDT)')}</Th>
                    <Th>{t('네트워크')}</Th>
                    <Th>{t('주소')}</Th>
                    <Th>TXID</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {recent.map((h) => {
                    const meta = statusMeta[h.type] || {
                      label: h.type,
                      color: 'gray'
                    }
                    const amt = new BigNumber(h.withdrawalAmount || 0).toFormat(
                      0
                    )
                    return (
                      <Tr key={h.id}>
                        <Td>
                          {h.type === 'WD_WAIT' ? (
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => openCancel(h.id)}
                            >
                              {t('취소')}
                            </Button>
                          ) : (
                            <Tooltip label={t('대기 상태에서만 취소 가능')}>
                              <Badge colorScheme="gray">{t('불가')}</Badge>
                            </Tooltip>
                          )}
                        </Td>
                        <Td whiteSpace="nowrap">
                          {dayjs(h.createdAt).format('YYYY-MM-DD HH:mm')}
                        </Td>
                        <Td>
                          <Badge colorScheme={meta.color}>{meta.label}</Badge>
                        </Td>
                        <Td isNumeric>{amt}</Td>
                        <Td>{h.network || '-'}</Td>
                        <Td>{h.walletAddress || '-'}</Td>
                        <Td maxW="260px">
                          {h.txid ? (
                            <HStack spacing={1}>
                              {getExplorerTxUrl(h.network, h.txid) ? (
                                <Tooltip label={h.txid}>
                                  <HStack
                                    as={Link}
                                    href={getExplorerTxUrl(h.network, h.txid)!}
                                    isExternal
                                    maxW="200px"
                                  >
                                    <Text isTruncated color="blue.300">
                                      {h.txid}
                                    </Text>
                                    <FiExternalLink />
                                  </HStack>
                                </Tooltip>
                              ) : (
                                <Tooltip label={h.txid}>
                                  <Text maxW="220px" isTruncated>
                                    {h.txid}
                                  </Text>
                                </Tooltip>
                              )}
                              <CopyTxButton value={h.txid} />
                            </HStack>
                          ) : (
                            '-'
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            )}
          </Box>
        </VStack>
      </Container>

      {/* 취소 다이얼로그 */}
      <AlertDialog
        isOpen={isCancelOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeCancel}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="dark.card" borderColor="dark.border" borderWidth="1px">
            <AlertDialogHeader>{t('출금신청 취소')}</AlertDialogHeader>
            <AlertDialogBody>
              {t('출금신청을 취소하시겠습니까?')}
              <br />
              {t('취소 시 출금신청 시 차감된 금액이 반환됩니다.')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeCancel} variant="ghost">
                {t('닫기')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={handleCancelWithdrawal}
                isLoading={isCancelling}
                loadingText={t('취소 중...')}
              >
                {t('출금 취소')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default Withdrawal
